import React, { useContext, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/cartpage.scss";

const PROMO_CODE = "SLOW10";
const PROMO_RATE = 0.1;

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity } =
    useContext(CartContext);
  const [promoInput, setPromoInput] = useState("");
  const [appliedPromo, setAppliedPromo] = useState("");
  const [promoFeedback, setPromoFeedback] = useState(null);

  const lines = useMemo(
    () =>
      cartItems
        .map((item) => ({
          ...item,
          product: productData.find((product) => product.id === item.id),
        }))
        .filter((item) => item.product),
    [cartItems]
  );

  const itemCount = lines.reduce((count, item) => count + item.quantity, 0);
  const subtotal = lines.reduce(
    (total, item) => total + item.product.price * item.quantity,
    0
  );
  const discount = appliedPromo ? subtotal * PROMO_RATE : 0;
  const shipping =
    subtotal >= siteConfig.shipping.freeThreshold
      ? 0
      : siteConfig.shipping.standardRate;
  const total = Math.max(0, subtotal - discount + shipping);
  const freeShippingRemaining = Math.max(
    0,
    siteConfig.shipping.freeThreshold - subtotal
  );

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePromoSubmit = (event) => {
    event.preventDefault();
    const normalizedCode = promoInput.trim().toUpperCase();

    if (normalizedCode === PROMO_CODE) {
      setAppliedPromo(PROMO_CODE);
      setPromoInput(PROMO_CODE);
      setPromoFeedback({
        type: "success",
        message: "10% slow-fashion welcome discount applied.",
      });
      return;
    }

    setAppliedPromo("");
    setPromoFeedback({
      type: "error",
      message: normalizedCode
        ? "This code is not available. Try SLOW10 for this demo."
        : "Enter a discount code first.",
    });
  };

  const handleCheckout = () => {
    navigate("/checkout", {
      state: { discountCode: appliedPromo || undefined },
    });
    scrollToTop();
  };

  if (lines.length === 0) {
    return (
      <main className="cart-page cart-page--empty" id="main-content">
        <section className="cart-page__empty" aria-labelledby="empty-cart-title">
          <p className="cart-page__eyebrow">Your selection</p>
          <h1 id="empty-cart-title">Your shopping bag is empty</h1>
          <p>
            Discover everyday pieces with clear fit, colour and material
            information.
          </p>
          <Link
            to="/shop"
            onClick={scrollToTop}
            className="cart-page__primary-link"
          >
            Explore the collection
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="cart-page" id="main-content">
      <div className="cart-page__container">
        <header className="cart-page__header">
          <p className="cart-page__eyebrow">Your selection</p>
          <div className="cart-page__heading-row">
            <h1>Shopping bag</h1>
            <span>
              {itemCount} {itemCount === 1 ? "item" : "items"}
            </span>
          </div>
        </header>

        <div className="cart-page__layout">
          <section className="cart-page__items" aria-label="Bag items">
            {lines.map(({ product, quantity, size, color }) => {
              const lineTotal = product.price * quantity;
              const itemKey = `${product.id}-${size}-${color}`;

              return (
                <article className="cart-item" key={itemKey}>
                  <Link
                    className="cart-item__image-link"
                    to={`/shopDetails/${product.id}`}
                    onClick={scrollToTop}
                    aria-label={`View ${product.name}`}
                  >
                    <img
                      src={product.image || product.images?.[0]}
                      alt={product.name}
                      loading="lazy"
                      decoding="async"
                    />
                  </Link>

                  <div className="cart-item__details">
                    <p className="cart-item__category">
                      {product.gender} · {product.category}
                    </p>
                    <h2>
                      <Link
                        to={`/shopDetails/${product.id}`}
                        onClick={scrollToTop}
                      >
                        {product.name}
                      </Link>
                    </h2>
                    <dl className="cart-item__options">
                      {color && (
                        <div>
                          <dt>Colour</dt>
                          <dd>{color}</dd>
                        </div>
                      )}
                      {size && (
                        <div>
                          <dt>Size</dt>
                          <dd>{size}</dd>
                        </div>
                      )}
                    </dl>
                    <p className="cart-item__unit-price">
                      {formatPrice(product.price)} each
                    </p>
                    <button
                      type="button"
                      className="cart-item__remove"
                      onClick={() =>
                        removeFromCart(product.id, size, color)
                      }
                    >
                      Remove
                    </button>
                  </div>

                  <div className="cart-item__purchase">
                    <div
                      className="quantity-control"
                      aria-label={`Quantity for ${product.name}`}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          updateQuantity(product.id, size, color, quantity - 1)
                        }
                        disabled={quantity <= 1}
                        aria-label={`Decrease ${product.name} quantity`}
                      >
                        −
                      </button>
                      <span aria-live="polite">{quantity}</span>
                      <button
                        type="button"
                        onClick={() =>
                          updateQuantity(product.id, size, color, quantity + 1)
                        }
                        aria-label={`Increase ${product.name} quantity`}
                      >
                        +
                      </button>
                    </div>
                    <strong>{formatPrice(lineTotal)}</strong>
                  </div>
                </article>
              );
            })}

            <Link
              to="/shop"
              onClick={scrollToTop}
              className="cart-page__text-link"
            >
              ← Continue shopping
            </Link>
          </section>

          <aside className="cart-page__aside" aria-label="Order summary">
            <div className="cart-summary">
              <h2>Order summary</h2>

              <div className="cart-summary__shipping-note">
                {freeShippingRemaining > 0 ? (
                  <>
                    Add {formatPrice(freeShippingRemaining)} more for free
                    standard delivery.
                  </>
                ) : (
                  <>Your order qualifies for free standard delivery.</>
                )}
              </div>

              <form className="cart-promo" onSubmit={handlePromoSubmit}>
                <label htmlFor="cart-promo-code">Discount code</label>
                <div className="cart-promo__controls">
                  <input
                    id="cart-promo-code"
                    type="text"
                    value={promoInput}
                    onChange={(event) => {
                      setPromoInput(event.target.value);
                      setAppliedPromo("");
                      setPromoFeedback(null);
                    }}
                    placeholder="Enter code"
                    autoComplete="off"
                  />
                  <button type="submit">Apply</button>
                </div>
                {promoFeedback && (
                  <p
                    className={`cart-promo__feedback cart-promo__feedback--${promoFeedback.type}`}
                    role={promoFeedback.type === "error" ? "alert" : "status"}
                  >
                    {promoFeedback.message}
                  </p>
                )}
              </form>

              <dl className="cart-summary__totals">
                <div>
                  <dt>Subtotal</dt>
                  <dd>{formatPrice(subtotal)}</dd>
                </div>
                {discount > 0 && (
                  <div className="cart-summary__discount">
                    <dt>Discount ({PROMO_CODE})</dt>
                    <dd>−{formatPrice(discount)}</dd>
                  </div>
                )}
                <div>
                  <dt>Standard delivery</dt>
                  <dd>{shipping === 0 ? "Free" : formatPrice(shipping)}</dd>
                </div>
                <div className="cart-summary__total">
                  <dt>Total</dt>
                  <dd>{formatPrice(total)}</dd>
                </div>
              </dl>

              <p className="cart-summary__tax-note">
                VAT included. Delivery is calculated from the order subtotal.
              </p>
              <button
                type="button"
                className="cart-summary__checkout"
                onClick={handleCheckout}
              >
                Continue to checkout
              </button>
              <p className="cart-summary__secure-note">
                Secure checkout · {siteConfig.shipping.returnsWindow}-day
                returns
              </p>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default CartPage;
